import React, { useState, useEffect } from "react";
import { WishlistButton, PlaceholderImage } from "./../";


import {
  defaultProduct,
  basket,
  wishlist,
  wishlist_full,
  view
} from "./images";

interface ProductImageProps {
  id: string;
  name: string;
  imageSrc: string;
  defaultProduct: string;
}

const ProductImage: React.FunctionComponent<ProductImageProps> = ({ id, name, imageSrc, defaultProduct }) => {
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => setCurrentImage(imageSrc);
    img.onerror = () => setCurrentImage(defaultProduct);
  }, [imageSrc, defaultProduct]);

  return (
    <>
      {!currentImage ? (
        <PlaceholderImage width={300} height={368} borderRadius={10} />
      ) : (
        <img
          src={currentImage}
          alt={name}
          style={{
            width: "100%",
            height: 368,
            objectFit: "cover",
            borderRadius: 10,
          }}
          loading="lazy"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      )}
    </>
  );
};

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  discount?: string;
  isNew?: boolean;
  isInWishlist?: boolean;
  toggleWishlist: () => void;
  onClickProduct: () => void;
  onClickAddToCart: () => void;
}

const ShopProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  oldPrice,
  image,
  discount,
  isNew,
  isInWishlist,
  toggleWishlist,
  onClickProduct,
  onClickAddToCart,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyle: React.CSSProperties = {
    position: "relative",
    background: "trasnparent",
    overflow: "hidden",
    textAlign: "left",
    transition: "transform 0.3s ease-in-out",
    transform: isHovered ? "scale(1.05)" : "scale(1)",
    marginBottom: 20,
    padding: 15,
  };

  const imageContainerStyle: React.CSSProperties = {
    position: "relative",
    overflow: "hidden",
  };

  const badgeContainerStyle: React.CSSProperties = {
    position: "absolute",
    top: "10px",
    left: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  };

  const badgeStyle: React.CSSProperties = {
    padding: "4px 8px",
    fontSize: "12px",
    fontWeight: "bold",
    color: "#fff",
    borderRadius: "4px",
  };

  const discountBadgeStyle: React.CSSProperties = {
    ...badgeStyle,
    backgroundColor: "rgb(119, 160, 74)",
  };

  const newBadgeStyle: React.CSSProperties = {
    ...badgeStyle,
    backgroundColor: "rgb(166, 124, 82)",
  };

  const hoverActionsStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    gap: "10px",
    opacity: isHovered ? 1 : 0,
    transition: "opacity 0.3s ease-in-out",
  };
  
  const actionButtonStyle: React.CSSProperties = {
    backgroundColor: "#F6F4F2",
    padding: "10px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s ease-in-out, background-color 0.3s",
    transform: isHovered ? "scaleX(1) scaleY(1)" : "scaleX(0.2) scaleY(0.1)",
  };

  const detailsStyle: React.CSSProperties = {
    margin: "20px 0px 0px 0px"
  };

  const priceContainerStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "10px",
    fontSize: "18px",
    fontWeight: "bold",
    color: "rgb(195, 157, 99)",
  };

  const oldPriceStyle: React.CSSProperties = {
    textDecoration: "line-through",
    color: "#9ca3af",
    fontSize: "14px",
  };

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Produktbild */}
      <div style={imageContainerStyle}>
        <ProductImage 
          id={id}
          name={name}
          imageSrc={image}
          defaultProduct={defaultProduct}
        />
        {/* Badges */}
        <div style={badgeContainerStyle}>
          {discount && <span style={discountBadgeStyle}>{discount}</span>}
          {isNew && <span style={newBadgeStyle}>New</span>}
        </div>

        {/* Hover-Aktionen */}
        <div style={hoverActionsStyle}>
          <button style={actionButtonStyle} title="Add to cart" onClick={onClickAddToCart}><img height={30} width={30} src={basket} alt="basket" /></button>
          <button style={actionButtonStyle} title="Quick View" onClick={onClickProduct}><img height={30} width={30} src={view} alt="view" /></button>
          <button style={actionButtonStyle} title="Add to wishlist" onClick={toggleWishlist}><img height={30} width={30} src={isInWishlist ? wishlist_full : wishlist} alt="wishlist" /></button>
        </div>
      </div>

      {/* Produktdetails */}
      <div style={detailsStyle}>
        <h3>
          <a href={`/product/${id}`} style={{ fontFamily: "Avenir Next Codensed Ultralight", textDecoration: "none", color: "#000" }}>
            {name}
          </a>
        </h3>
        <div style={priceContainerStyle}>
          <span>€{price.toFixed(2)}</span>
          {oldPrice && <span style={oldPriceStyle}>€{oldPrice.toFixed(2)}</span>}
          {/* Wishlist-Button */}
          <span style={{marginLeft: 'auto'}}><WishlistButton onClick={toggleWishlist} isSelected={!!isInWishlist} /></span>
        </div>
      </div>
    </div>
  );
};

export default ShopProductCard;
